import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import templateService from "../../../services/template.service";
import clinicService from "../../../services/clinic.service";
import Loading from "../../common/Loading";
import Swal from 'sweetalert2';

const EditClinicSmsTemplate = () => {
    const { clinicId, id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [template, setTemplate] = useState(null);
    const [clinic, setClinic] = useState(null);
    const [formData, setFormData] = useState({
        content: "",
        status: true,
        reminderName: "",
        reminderTypeId: ""
    });

    const fetchTemplateData = async () => {
        try {
            setLoading(true);

            // Get clinic data first
            const clinicResponse = await clinicService.getClinicById(clinicId);
            setClinic(clinicResponse.data);

            // Get template data
            const templateResponse = await templateService.getSmsTemplateById(clinicId, id);
            const templateData = templateResponse.data;
            setTemplate(templateData);

            // Set form data
            setFormData({
                content: templateData.content || "",
                status: templateData.status,
                reminderName: templateData.reminderName || "",
                reminderTypeId: templateData.reminderTypeId || ""
            });

        } catch (error) {
            console.error('Error fetching template data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load template data. Please try again.',
                confirmButtonColor: '#dc3545'
            }).then(() => {
                navigate('/clinic-templates');
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clinicId && id) {
            fetchTemplateData();
        }
    }, [clinicId, id, navigate]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);

            // Prepare the request data including templateId in the body
            const requestData = {
                id: id, // Include templateId in the body
                content: formData.content,
                status: formData.status,
                reminderName: formData.reminderName,
                reminderTypeId: formData.reminderTypeId,
                clinicId: clinicId // Include clinicId in the body if needed
            };

            await templateService.updateSmsTemplate(clinicId, id, requestData);

            // Fetch the updated template data from the server to ensure UI reflects the latest state
            await fetchTemplateData();

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'SMS template updated successfully.',
                confirmButtonColor: '#198754',
                timer: 2000
            });

        } catch (error) {
            console.error('Error updating template:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update template. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate('/clinic-templates');
    };

    if (loading) {
        return (
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-pencil me-2"></i> Edit SMS Template</h1>
                </div>
                <Loading />
            </div>
        );
    }

    return (
        <div className="container container-xxl">
            <div className="pagetitle d-flex justify-content-between align-items-center">
                <h1><i className="bi bi-pencil me-2"></i> Edit SMS Template</h1>
                <a className="btn btn-outline-secondary btn-sm" href="/clinic-templates">
                    <i className="bi bi-chevron-left"></i> Back
                </a>
            </div>

            <div className="row">
                <div className="col-lg-8">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title">
                                <i className="bi bi-chat-dots me-2"></i>
                                Template Details
                            </h5>

                            {clinic && (
                                <div className="alert alert-info">
                                    <i className="bi bi-building me-2"></i>
                                    <strong>Clinic:</strong> {clinic.name} {clinic.city && `- ${clinic.city}`}
                                    <br />
                                    <small className="text-muted">Clinic ID: {clinic.id}</small>
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Reminder Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="reminderName"
                                            value={formData.reminderName}
                                            disabled
                                        />
                                        <div className="form-text">
                                            Descriptive name for this template (e.g., BIRTHDAY, APPOINTMENT_REMINDER)
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Status</label>
                                        <div className="form-check form-group form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="status"
                                                checked={formData.status}
                                                onChange={handleInputChange}
                                                style={{ width: '3em', height: '1.5em' }}
                                            />
                                            <label className="form-check-label ms-3 mt-1">
                                                {formData.status ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                        <div className="form-text">
                                            Active templates will be used for automated reminders
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Template Content</label>
                                    <textarea
                                        className="form-control"
                                        name="content"
                                        value={formData.content}
                                        onChange={handleInputChange}
                                        required
                                        rows="6"
                                        placeholder="Enter your SMS template content here..."
                                        style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                    />
                                    <div className="form-text">
                                        Use <code>{'{PATIENT_NAME}'}</code> for patient name placeholder.
                                        Maximum 480 characters recommended for SMS.
                                    </div>
                                    <div className="mt-2">
                                        <span className={`badge ${formData.content.length <= 480 ? 'bg-success' : 'bg-warning'}`}>
                                            {formData.content.length} characters
                                        </span>
                                        {formData.content.length > 480 && (
                                            <span className="badge bg-warning ms-2">
                                                Message may be split into multiple parts
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Template Preview</label>
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="bi bi-phone text-muted me-2"></i>
                                                <small className="text-muted">SMS Preview:</small>
                                            </div>
                                            <div className="bg-white p-3 rounded border">
                                                {formData.content || (
                                                    <span className="text-muted">Preview will appear here...</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={handleCancel}
                                        disabled={saving}
                                    >
                                        <i className="bi bi-x-circle me-2"></i>
                                        Cancel
                                    </button>
                                    <div className="d-flex gap-2">
                                        <button
                                            type="submit"
                                            className="btn btn-sm btn-success"
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-floppy me-2"></i>
                                                    Save
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-body">
                            <h6 className="card-title">
                                <i className="bi bi-info-circle me-2"></i>
                                Template Information
                            </h6>

                            <div className="mb-3">
                                <small className="text-muted">Template ID</small>
                                <div className="fw-semibold">{template.id}</div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Created</small>
                                <div className="fw-semibold">{template.createdAt}</div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Last Modified</small>
                                <div className="fw-semibold">{template.modifiedAt}</div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Reminder Type</small>
                                <div className="fw-semibold">{template.reminderTypeId}</div>
                            </div>

                            <div className="alert alert-warning">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                <strong>Note:</strong> Changes to templates may affect scheduled reminders.
                            </div>
                        </div>
                    </div>

                    <div className="card mt-3">
                        <div className="card-body">
                            <h6 className="card-title">
                                <i className="bi bi-lightbulb me-2"></i>
                                SMS Best Practices
                            </h6>
                            <ul className="list-unstyled small">
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Keep messages under 480 characters
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Include clear call-to-action
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Personalize with patient name
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Include opt-out instructions
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditClinicSmsTemplate;